import { addTimeToDate, concatTZDateTimeFromObject, formatToUTC } from 'libs/dateTimeHelpers';
import { useCountries } from 'hooks/common/useCountries';
import { useRegions } from 'hooks/common/useRegions';
import { useSpots } from './useSpots';
import { useEffect, useMemo, useRef } from 'react';
import { IReservationValues } from './Public/Create';
import {
  addDays,
  addHours,
  addMinutes,
  endOfDay,
  isBefore,
  isEqual,
  isSameDay,
  max,
  min,
  startOfDay,
} from 'date-fns';

interface IUserCreateReservation {
  guid?: string;
  values: IReservationValues;
  setFieldValue: any;
  parking_can_reservation_days?: number;
  parking_time_limit?: number;
  parking_start_working?: string | null;
  parking_end_working?: string | null;
  isEditForm?: boolean;
  location_country_id: number;
}

export const GuestOptions = [
  { label: 'Visitor', value: 'visitor' },
  { label: 'Vendor', value: 'vendor' },
];

export function useCreateReservation({
  guid,
  values,
  setFieldValue,
  parking_can_reservation_days,
  parking_time_limit,
  parking_start_working,
  parking_end_working,
  isEditForm,
  location_country_id,
}: IUserCreateReservation) {
  const { countries } = useCountries(true, { labelKey: 'title', valueKey: 'id' });
  const { regions, fetchRegions } = useRegions(false, { labelKey: 'title', valueKey: 'id' });
  const { spots, fetchSpots } = useSpots({ guid, except_id: values.id });

  useEffect(() => {
    if (values.start_date && values.start_time && values.end_date && values.end_time) {
      const start_time = concatTZDateTimeFromObject<IReservationValues>(
        values,
        ['start_date'],
        ['start_time']
      );
      const end_time = concatTZDateTimeFromObject<IReservationValues>(
        values,
        ['end_date'],
        ['end_time']
      );
      fetchSpots({ start_time, end_time, guid, except_id: values.id });
    }
  }, [
    values.start_date,
    values.start_time,
    values.end_date,
    values.end_time,
    values.id,
    fetchSpots,
    guid,
  ]);

  useEffect(() => {
    if (values.parking_spot_id && spots.length > 0) {
      const chosen_spot = spots.find((spot) => spot.id === values.parking_spot_id);
      !chosen_spot && setFieldValue('parking_spot_id', null);
    }
  }, [spots, values.parking_spot_id]);

  useEffect(() => {
    if (values.location_country_id) {
      const country = countries.find((c) => c.id === values.location_country_id);
      if (country) {
        fetchRegions({ countryId: country.short_code });
      }
    }
  }, [values.location_country_id, countries]);

  const prevCountryRef = useRef(values.location_country_id);
  const isInitialLoadRef = useRef(!values.location_country_id);
  useEffect(() => {
    if (prevCountryRef.current !== values.location_country_id) {
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      } else {
        setFieldValue('location_region_id', null);
      }
      prevCountryRef.current = values.location_country_id;
    }
  }, [values.location_country_id]);

  const maxAllowDayForReservation = useMemo(
    () =>
      parking_can_reservation_days == null
        ? undefined
        : addDays(new Date(), parking_can_reservation_days),
    [parking_can_reservation_days]
  );

  const startDate = useMemo(() => {
    let startDate = values.start_date;
    if (values.start_date && values.start_time) {
      startDate = formatToUTC(
        concatTZDateTimeFromObject<IReservationValues>(values, ['start_date'], ['start_time'])
      );
    }
    return startDate;
  }, [values.start_date, values.start_time]);

  const endDate = useMemo(() => {
    let endDate = values.end_date;
    if (values.end_date && values.end_time) {
      endDate = formatToUTC(
        concatTZDateTimeFromObject<IReservationValues>(values, ['end_date'], ['end_time'])
      );
    }
    return endDate;
  }, [values.end_date, values.end_time]);

  const minEndDate = useMemo(() => {
    let baseStart = new Date();
    if (startDate) {
      const minDate = addMinutes(startDate, 15);

      baseStart =
        parking_can_reservation_days === 0 ? min([minDate, endOfDay(startDate)]) : minDate;
    }
    return baseStart;
  }, [startDate, parking_can_reservation_days]);

  const maxEndDate = useMemo(() => {
    const baseEnd =
      startDate && parking_time_limit
        ? addHours(startDate, parking_time_limit)
        : maxAllowDayForReservation;

    if (!baseEnd) return baseEnd;

    return parking_can_reservation_days === 0
      ? min([baseEnd, endOfDay(startDate ?? new Date())])
      : baseEnd;
  }, [startDate, parking_time_limit, maxAllowDayForReservation, parking_can_reservation_days]);

  const minEndTime = useMemo(() => {
    if (!startDate) return null;

    const candidate = addMinutes(startDate, 15);

    if (parking_can_reservation_days === 0) {
      const endOfToday = endOfDay(startDate);
      return min([candidate, endOfToday]);
    }

    if (endDate && isBefore(startOfDay(startDate), startOfDay(endDate))) {
      return startOfDay(endDate);
    }

    return candidate;
  }, [startDate, endDate, parking_time_limit, parking_can_reservation_days]);

  const maxEndTime = useMemo(() => {
    let finalMax: Date | null = maxEndDate ?? null;

    if (parking_can_reservation_days === 0) {
      if (!startDate) return null;

      const limitEnd = parking_time_limit
        ? addHours(startDate, parking_time_limit)
        : endOfDay(startDate);

      finalMax = min([limitEnd, endOfDay(startDate)]);
    } else if (!parking_time_limit) {
      finalMax = endDate ? endOfDay(endDate) : null;
    } else if (startDate && maxEndDate) {
      if (isEqual(startOfDay(startDate), startOfDay(maxEndDate))) {
        finalMax = maxEndDate;
      } else if (endDate && isEqual(endOfDay(startDate), endOfDay(endDate))) {
        finalMax = endOfDay(startDate);
      } else {
        finalMax = maxEndDate;
      }
    }

    return finalMax;
  }, [startDate, endDate, maxEndDate, parking_time_limit, parking_can_reservation_days]);

  const constrainWorkingHours =
    !isEditForm && Boolean(parking_start_working) && Boolean(parking_end_working);

  const sameDayReservation = useMemo(
    () =>
      Boolean(
        values.start_date && values.end_date && isSameDay(values.start_date, values.end_date)
      ),
    [values.start_date, values.end_date]
  );

  const workingOpenAt = useMemo(
    () =>
      constrainWorkingHours && sameDayReservation && startDate
        ? addTimeToDate(startDate, parking_start_working as string)
        : null,
    [constrainWorkingHours, sameDayReservation, startDate, parking_start_working]
  );

  const workingCloseAt = useMemo(
    () =>
      constrainWorkingHours && sameDayReservation && startDate
        ? addTimeToDate(startDate, parking_end_working as string)
        : null,
    [constrainWorkingHours, sameDayReservation, startDate, parking_end_working]
  );

  const minEndTimeWithinHours = useMemo(() => {
    if (!workingOpenAt) return minEndTime;
    return minEndTime ? max([minEndTime, workingOpenAt]) : workingOpenAt;
  }, [minEndTime, workingOpenAt]);

  const maxEndTimeWithinHours = useMemo(() => {
    if (!workingCloseAt) return maxEndTime;
    return maxEndTime ? min([maxEndTime, workingCloseAt]) : workingCloseAt;
  }, [maxEndTime, workingCloseAt]);

  const minStartTime = constrainWorkingHours ? parking_start_working : undefined;
  const maxStartTime = constrainWorkingHours ? parking_end_working : undefined;

  const countriesForAccount = useMemo(() => {
    const countriesForAccount = [...countries];
    const accountCountryIndex = countriesForAccount.findIndex(
      (country) => location_country_id === country.id
    );

    if (accountCountryIndex !== -1) {
      const [country] = countriesForAccount.splice(accountCountryIndex, 1);
      countriesForAccount.unshift(country);
    }
    return countriesForAccount;
  }, [countries, location_country_id]);

  const hasRegions = regions.length > 0;

  return {
    countries: countriesForAccount,
    regions,
    hasRegions,
    spots,
    maxAllowDayForReservation,
    minEndDate,
    maxEndDate,
    minStartTime,
    maxStartTime,
    minEndTime: minEndTimeWithinHours,
    maxEndTime: maxEndTimeWithinHours,
  };
}
